import { RESPONSE_STATUSES } from '../stores/ResponseStatus.js';

/**
 * CandidateTable — accordion-style candidate grid with expandable detail rows.
 * Compact rows show name, status dot, scores, disposition, response badge.
 * Expanded rows show contact info, email thread, actions, flags, notes, score details.
 */
export class CandidateTable {
    constructor(container, options = {}) {
        this._container = container;
        this._candidateStore = options.candidateStore;
        this._promptStore = options.promptStore;
        this._emailStore = options.emailStore || null;
        this._selectedPrompts = []; // prompt IDs active for this project
        this._candidates = [];
        this._sortField = 'name';
        this._sortDir = 'asc';
        this._searchQuery = '';
        this._columnVisibility = this._defaultVisibility();
        this._columnWidths = {};
        this._onDispositionChange = options.onDispositionChange || (() => {});
        this._onFlagChange = options.onFlagChange || (() => {});
        this._onDelete = options.onDelete || (() => {});
        this._onRetryScore = options.onRetryScore || (() => {});
        this._selectedCandidateId = null;
        this._resumeWindows = new Map();
        this._minimizedCols = new Set();
        this._avgPromptIds = new Set();
        this._hideRejected = false;
        this._expandedIds = new Set();
        this._emails = []; // all emails for current project
        this._el = null;

        // Subscribe to email store if provided
        if (this._emailStore) {
            this._emailStore.onChange((emails) => {
                this._emails = emails || [];
                this.render();
            });
        }
    }

    get element() {
        if (!this._el) {
            this._el = document.createElement('div');
            this._el.style.cssText = 'display:flex;flex-direction:column;flex:1;min-height:0;';
            // When user finishes editing a field, do the deferred render
            this._el.addEventListener('focusout', () => {
                if (this._pendingRender) {
                    setTimeout(() => this.render(), 50);
                }
            });
        }
        return this._el;
    }

    setSelectedPrompts(promptIds) {
        this._selectedPrompts = promptIds || [];
        this.render();
    }

    setCandidates(candidates) {
        this._candidates = candidates || [];
        this.render();
    }

    setHideRejected(hide) {
        this._hideRejected = hide;
        this.render();
    }

    setSearchQuery(query) {
        this._searchQuery = (query || '').toLowerCase();
        this.render();
    }

    toggleColumn(colId) {
        this._columnVisibility[colId] = !this._columnVisibility[colId];
        this.render();
    }

    getColumnVisibility() { return { ...this._columnVisibility }; }

    getVisibleCount() {
        return this._getFilteredCandidates().length;
    }

    render() {
        // Skip re-render if an input is focused (prevents bouncing while editing)
        const active = document.activeElement;
        if (active && this._el && this._el.contains(active)) {
            const tag = active.tagName;
            if (['INPUT', 'SELECT', 'TEXTAREA'].includes(tag)) {
                this._pendingRender = true;
                return;
            }
        }
        this._pendingRender = false;

        const el = this.element;
        const wrapper = el.querySelector('.rm-table-wrapper');
        const scrollTop = wrapper?.scrollTop || 0;
        const scrollLeft = wrapper?.scrollLeft || 0;
        el.innerHTML = '';

        const candidates = this._getFilteredCandidates();
        if (candidates.length === 0 && this._candidates.length === 0) {
            el.innerHTML = `
                <div class="rm-empty-state">
                    <div class="rm-empty-state-icon">📋</div>
                    <div>No candidates yet — import resumes to get started</div>
                </div>`;
            return;
        }

        const newWrapper = document.createElement('div');
        newWrapper.className = 'rm-table-wrapper';

        const table = document.createElement('table');
        table.className = 'rm-table';

        // Header
        const thead = document.createElement('thead');
        const headerRow = document.createElement('tr');
        this._buildHeaderCells(headerRow);
        thead.appendChild(headerRow);
        table.appendChild(thead);

        // Body
        const tbody = document.createElement('tbody');
        for (const c of candidates) {
            tbody.appendChild(this._buildRow(c));
            // Expanded detail row
            if (this._expandedIds.has(c.id)) {
                tbody.appendChild(this._buildDetailRow(c));
            }
        }
        table.appendChild(tbody);

        newWrapper.appendChild(table);
        el.appendChild(newWrapper);

        // Restore scroll position
        newWrapper.scrollTop = scrollTop;
        newWrapper.scrollLeft = scrollLeft;
    }

    _buildHeaderCells(row) {
        const cols = this._getColumns();
        for (const col of cols) {
            if (this._columnVisibility[col.id] === false) continue;
            const th = document.createElement('th');
            th.className = col.className || '';
            th.dataset.sort = col.id;
            if (this._sortField === col.id) {
                th.classList.add(this._sortDir === 'asc' ? 'sort-asc' : 'sort-desc');
            }

            const isMinimized = this._minimizedCols.has(col.id);

            // Saved width
            if (this._columnWidths?.[col.id]) {
                th.style.width = this._columnWidths[col.id] + 'px';
            }
            if (isMinimized) {
                th.classList.add('rm-col-minimized');
                th.title = col.label + ' (double-click to expand)';
            }

            // No sort/resize for expand column
            if (col.id === 'expand') {
                const label = document.createElement('span');
                label.textContent = '';
                th.appendChild(label);
                row.appendChild(th);
                continue;
            }

            // Avg checkbox for score columns
            if (col.hasAvgCheckbox && !isMinimized) {
                const cb = document.createElement('input');
                cb.type = 'checkbox';
                cb.className = 'rm-avg-checkbox';
                cb.checked = this._avgPromptIds.has(col.promptId);
                cb.title = 'Include in average';
                cb.addEventListener('click', (e) => e.stopPropagation());
                cb.addEventListener('change', () => {
                    if (cb.checked) this._avgPromptIds.add(col.promptId);
                    else this._avgPromptIds.delete(col.promptId);
                    this.render();
                });
                th.appendChild(cb);
            }

            const label = document.createElement('span');
            label.textContent = isMinimized ? col.label.charAt(0) : col.label;
            th.appendChild(label);

            // Sort on click, minimize on double-click
            label.addEventListener('click', () => this._toggleSort(col.id));
            th.addEventListener('dblclick', (e) => {
                e.preventDefault();
                if (this._minimizedCols.has(col.id)) {
                    this._minimizedCols.delete(col.id);
                    delete this._columnWidths[col.id];
                } else {
                    this._minimizedCols.add(col.id);
                    this._columnWidths[col.id] = 30;
                }
                this.render();
            });

            // Resize handle
            if (col.label) {
                const handle = document.createElement('div');
                handle.className = 'rm-col-resize';
                handle.addEventListener('mousedown', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this._startResize(e, th, col.id);
                });
                th.appendChild(handle);
            }

            row.appendChild(th);
        }
    }

    _buildRow(c) {
        const tr = document.createElement('tr');
        tr.className = 'rm-compact-row';
        if (c.id === this._selectedCandidateId) {
            tr.classList.add('rm-row-selected');
        }
        if (this._expandedIds.has(c.id)) {
            tr.classList.add('rm-row-expanded');
        }
        tr.addEventListener('click', (e) => {
            const tag = e.target.tagName;
            if (['INPUT', 'SELECT', 'BUTTON', 'A'].includes(tag)) return;
            this._selectedCandidateId = (this._selectedCandidateId === c.id) ? null : c.id;
            this._el.querySelectorAll('.rm-row-selected').forEach(r => r.classList.remove('rm-row-selected'));
            if (this._selectedCandidateId === c.id) tr.classList.add('rm-row-selected');
        });
        const cols = this._getColumns();

        for (const col of cols) {
            if (this._columnVisibility[col.id] === false) continue;
            const td = document.createElement('td');
            td.className = col.className || '';
            if (this._minimizedCols.has(col.id)) {
                td.classList.add('rm-col-minimized');
            }

            if (col.type === 'expand') {
                const btn = document.createElement('button');
                btn.className = 'rm-row-expand-btn';
                btn.textContent = this._expandedIds.has(c.id) ? '\u25BC' : '\u25B6';
                btn.title = this._expandedIds.has(c.id) ? 'Collapse' : 'Expand';
                btn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    if (this._expandedIds.has(c.id)) {
                        this._expandedIds.delete(c.id);
                    } else {
                        this._expandedIds.add(c.id);
                    }
                    this.render();
                });
                td.appendChild(btn);
            } else if (col.type === 'nameLink') {
                const nameSpan = document.createElement('span');
                nameSpan.style.cssText = 'display:flex;align-items:center;gap:4px;';

                const link = document.createElement('a');
                link.textContent = c[col.field] || c.fileName || '(unnamed)';
                link.title = `Click to view resume: ${c.fileName}`;
                link.href = '#';
                link.style.cssText = 'color:var(--primary);text-decoration:none;cursor:pointer;';
                link.addEventListener('click', (e) => {
                    e.preventDefault();
                    this._openResume(c);
                });
                nameSpan.appendChild(link);
                td.appendChild(nameSpan);
            } else if (col.type === 'statusDot') {
                td.appendChild(this._buildStatusDot(c));
                td.style.textAlign = 'center';
            } else if (col.type === 'score') {
                const scoreData = c.scores?.[col.promptId];
                td.innerHTML = this._renderScore(scoreData);
                td.style.textAlign = 'center';
                if (scoreData?.status === 'error') {
                    td.style.cursor = 'pointer';
                    td.title = 'Click to retry';
                    td.addEventListener('click', () => this._onRetryScore(c.id, col.promptId));
                }
            } else if (col.type === 'average') {
                td.innerHTML = this._renderAverage(c);
                td.style.textAlign = 'center';
            } else if (col.type === 'disposition') {
                td.appendChild(this._buildDispositionSelect(c));
            } else if (col.type === 'responseStatus') {
                td.innerHTML = this._renderResponseStatus(c);
            }

            tr.appendChild(td);
        }
        return tr;
    }

    _buildDetailRow(c) {
        const cols = this._getColumns().filter(col => this._columnVisibility[col.id] !== false);
        const colSpan = cols.length;

        const tr = document.createElement('tr');
        tr.className = 'rm-detail-row';

        const td = document.createElement('td');
        td.colSpan = colSpan;
        td.className = 'rm-detail-cell';

        const panel = document.createElement('div');
        panel.className = 'rm-detail-panel';

        // Left column (60%)
        const left = document.createElement('div');
        left.className = 'rm-detail-left';

        // Contact Info
        left.appendChild(this._buildDetailSection('Contact Info', () => {
            const div = document.createElement('div');
            div.className = 'rm-detail-contact';
            const fields = [
                ['Name', c.name || c.fileName || '(unnamed)'],
                ['Email', c.email || ''],
                ['Phone', c.phone || ''],
                ['Education', c.education || ''],
            ];
            for (const [label, val] of fields) {
                if (!val) continue;
                const row = document.createElement('div');
                row.className = 'rm-detail-field';
                row.innerHTML = `<span class="rm-detail-field-label">${label}:</span> <span class="rm-detail-field-value">${this._esc(val)}</span>`;
                div.appendChild(row);
            }
            return div;
        }));

        // Career Summary
        if (c.careerSummary) {
            left.appendChild(this._buildDetailSection('Career Summary', () => {
                const div = document.createElement('div');
                div.className = 'rm-detail-text';
                div.textContent = c.careerSummary;
                return div;
            }));
        }

        // Skills
        if (c.skillSummary) {
            left.appendChild(this._buildDetailSection('Skills', () => {
                const div = document.createElement('div');
                div.className = 'rm-detail-text';
                div.textContent = c.skillSummary;
                return div;
            }));
        }

        // Email Thread
        const candidateEmails = this._emails.filter(e => e.candidateId === c.id);
        if (candidateEmails.length > 0) {
            left.appendChild(this._buildDetailSection(`Email Thread (${candidateEmails.length})`, () => {
                return this._buildEmailThread(candidateEmails);
            }));
        }

        // Right column (40%)
        const right = document.createElement('div');
        right.className = 'rm-detail-right';

        // Actions
        right.appendChild(this._buildDetailSection('Actions', () => {
            const grid = document.createElement('div');
            grid.className = 'rm-action-buttons';

            // Interview date
            const dateLabel = document.createElement('label');
            dateLabel.className = 'rm-action-date-label';
            dateLabel.textContent = 'Interview Date:';
            grid.appendChild(dateLabel);

            const dateInput = document.createElement('input');
            dateInput.type = 'datetime-local';
            dateInput.className = 'rm-datetime-input';
            dateInput.step = '900';
            const existing = c.interviewDate;
            if (existing) {
                const d = existing.toDate ? existing.toDate() : new Date(existing);
                if (!isNaN(d)) dateInput.value = this._toLocalDatetime(d);
            } else {
                dateInput.addEventListener('focus', () => {
                    if (!dateInput.value) {
                        const now = new Date();
                        const next = new Date(now);
                        do { next.setDate(next.getDate() + 1); } while (next.getDay() === 0 || next.getDay() === 6);
                        next.setHours(16, 0, 0, 0);
                        dateInput.value = this._toLocalDatetime(next);
                    }
                }, { once: true });
            }
            dateInput.addEventListener('change', () => {
                const val = dateInput.value ? new Date(dateInput.value).toISOString() : '';
                this._onFlagChange(c.id, 'interviewDate', val);
            });
            grid.appendChild(dateInput);

            // View PDF
            const pdfBtn = document.createElement('button');
            pdfBtn.className = 'rm-btn';
            pdfBtn.textContent = 'View Original PDF';
            pdfBtn.addEventListener('click', () => this._openResume(c));
            grid.appendChild(pdfBtn);

            // View Markdown
            if (c.resumeMarkdown) {
                const mdBtn = document.createElement('button');
                mdBtn.className = 'rm-btn';
                mdBtn.textContent = 'View Markdown';
                mdBtn.addEventListener('click', () => this._openMarkdown(c));
                grid.appendChild(mdBtn);
            }

            // Delete
            const delBtn = document.createElement('button');
            delBtn.className = 'rm-btn rm-btn-danger';
            delBtn.textContent = 'Delete Candidate';
            delBtn.addEventListener('click', () => {
                if (confirm(`Delete candidate "${c.name || c.fileName}"?`)) {
                    this._onDelete(c.id, c.storagePath);
                }
            });
            grid.appendChild(delBtn);

            return grid;
        }));

        // Flags
        right.appendChild(this._buildDetailSection('Flags', () => {
            const div = document.createElement('div');
            div.className = 'rm-detail-flags';
            const flags = [
                ['followUp', 'Follow-up'],
                ['interview', 'Interview'],
                ['rejected', 'Rejected'],
                ['spider', 'Spider'],
            ];
            for (const [field, label] of flags) {
                const row = document.createElement('label');
                row.className = 'rm-detail-flag-row';
                const cb = document.createElement('input');
                cb.type = 'checkbox';
                cb.className = 'rm-checkbox';
                cb.checked = !!c[field];
                cb.addEventListener('change', () => {
                    this._onFlagChange(c.id, field, cb.checked);
                });
                row.appendChild(cb);
                const span = document.createElement('span');
                span.textContent = label;
                row.appendChild(span);
                div.appendChild(row);
            }
            return div;
        }));

        // Notes
        right.appendChild(this._buildDetailSection('Notes', () => {
            const textarea = document.createElement('textarea');
            textarea.className = 'rm-detail-notes';
            textarea.value = c.notes || '';
            textarea.placeholder = 'Add notes...';
            textarea.addEventListener('blur', () => {
                if (textarea.value !== (c.notes || '')) {
                    this._onFlagChange(c.id, 'notes', textarea.value);
                }
            });
            return textarea;
        }));

        // Score Details
        const prompts = this._promptStore?.getAll() || [];
        const scoreEntries = this._selectedPrompts
            .map(pid => ({ prompt: prompts.find(p => p.id === pid), scoreData: c.scores?.[pid] }))
            .filter(e => e.prompt && e.scoreData?.status === 'complete');

        if (scoreEntries.length > 0) {
            right.appendChild(this._buildDetailSection('Score Details', () => {
                const div = document.createElement('div');
                div.className = 'rm-score-details';
                for (const { prompt, scoreData } of scoreEntries) {
                    const item = document.createElement('div');
                    item.className = 'rm-score-detail';
                    const cls = scoreData.score >= 7 ? 'high' : scoreData.score >= 4 ? 'mid' : 'low';
                    item.innerHTML = `
                        <div class="rm-score-detail-header">
                            <span class="rm-score-detail-name">${this._esc(prompt.name)}</span>
                            <span class="rm-score rm-score-${cls}">${scoreData.score}</span>
                        </div>
                        <div class="rm-score-detail-reasoning">${this._esc(scoreData.reasoning || '')}</div>
                    `;
                    div.appendChild(item);
                }
                return div;
            }));
        }

        panel.appendChild(left);
        panel.appendChild(right);
        td.appendChild(panel);
        tr.appendChild(td);
        return tr;
    }

    _buildDetailSection(title, contentFn) {
        const section = document.createElement('div');
        section.className = 'rm-detail-section';
        const h4 = document.createElement('h4');
        h4.className = 'rm-detail-section-title';
        h4.textContent = title;
        section.appendChild(h4);
        section.appendChild(contentFn());
        return section;
    }

    _buildStatusDot(candidate) {
        const dot = document.createElement('span');
        dot.className = 'rm-status-dot';

        // Determine state
        const hasUnreadResponse = candidate.responseStatus &&
            candidate.responseStatus !== 'no_response';
        const hasDisposition = !!candidate.disposition;
        const isRejected = !!candidate.rejected || candidate.disposition === 'no';
        const hasScores = candidate.scores && Object.values(candidate.scores).some(s => s?.status === 'complete');
        const hasExtraction = candidate.extractionStatus === 'complete';
        const isExtracting = candidate.extractionStatus === 'processing';

        if (hasUnreadResponse) {
            dot.classList.add('rm-status-dot-orange');
            dot.title = 'Has response';
        } else if (isRejected) {
            dot.classList.add('rm-status-dot-red');
            dot.title = 'Rejected';
        } else if (hasDisposition) {
            dot.classList.add('rm-status-dot-green');
            dot.title = 'Disposition set';
        } else if (hasScores) {
            dot.classList.add('rm-status-dot-yellow');
            dot.title = 'Scored, needs review';
        } else if (hasExtraction) {
            dot.classList.add('rm-status-dot-blue');
            dot.title = 'Extracted, no scores';
        } else if (isExtracting) {
            dot.classList.add('rm-status-dot-blue');
            dot.title = 'Extracting...';
        } else {
            dot.classList.add('rm-status-dot-gray');
            dot.title = 'New / pending';
        }

        return dot;
    }

    _buildEmailThread(emails) {
        const container = document.createElement('div');
        container.className = 'rm-email-thread';

        for (const email of emails) {
            const item = document.createElement('div');
            item.className = 'rm-email-item';

            const isInbound = email.direction === 'inbound';
            const arrow = isInbound ? '\u2190' : '\u2192';
            const arrowClass = isInbound ? 'rm-email-inbound' : 'rm-email-outbound';

            // Header line
            const header = document.createElement('div');
            header.className = 'rm-email-item-header';

            const arrowSpan = document.createElement('span');
            arrowSpan.className = `rm-email-arrow ${arrowClass}`;
            arrowSpan.textContent = arrow;
            header.appendChild(arrowSpan);

            const dateSpan = document.createElement('span');
            dateSpan.className = 'rm-email-date';
            const dt = email.createdAt?.toDate ? email.createdAt.toDate() : (email.createdAt ? new Date(email.createdAt) : null);
            dateSpan.textContent = dt ? dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
            header.appendChild(dateSpan);

            const subjectSpan = document.createElement('span');
            subjectSpan.className = 'rm-email-subject';
            subjectSpan.textContent = email.subject || '(no subject)';
            header.appendChild(subjectSpan);

            // Status badge
            const statusBadge = document.createElement('span');
            statusBadge.className = 'rm-email-status-badge';
            const statusText = email.status || 'unknown';
            statusBadge.textContent = statusText;
            statusBadge.classList.add(`rm-email-status-${statusText}`);
            header.appendChild(statusBadge);

            // Inbound AI classification
            if (isInbound && email.classification) {
                const classLabel = document.createElement('span');
                classLabel.className = 'rm-email-classification';
                const info = RESPONSE_STATUSES[email.classification] || {};
                classLabel.textContent = info.label || email.classification;
                if (info.color) classLabel.style.background = info.color;
                header.appendChild(classLabel);
            }

            item.appendChild(header);

            // Truncated body (click to expand)
            if (email.body) {
                const bodyDiv = document.createElement('div');
                bodyDiv.className = 'rm-email-body-truncated';
                const preview = email.body.substring(0, 120).replace(/\n/g, ' ');
                bodyDiv.textContent = preview + (email.body.length > 120 ? '...' : '');
                bodyDiv.addEventListener('click', () => {
                    if (bodyDiv.classList.contains('rm-email-body-expanded')) {
                        bodyDiv.classList.remove('rm-email-body-expanded');
                        bodyDiv.textContent = preview + (email.body.length > 120 ? '...' : '');
                    } else {
                        bodyDiv.classList.add('rm-email-body-expanded');
                        bodyDiv.textContent = email.body;
                    }
                });
                item.appendChild(bodyDiv);
            }

            container.appendChild(item);
        }

        return container;
    }

    _getColumns() {
        const base = [
            { id: 'expand', label: '', type: 'expand', className: 'rm-col-expand' },
            { id: 'name', label: 'Name', field: 'name', type: 'nameLink', className: 'rm-col-name' },
            { id: 'status', label: '', type: 'statusDot', className: 'rm-col-status-dot' },
        ];

        // Add score columns for each selected prompt
        const prompts = this._promptStore?.getAll() || [];
        for (const pid of this._selectedPrompts) {
            const prompt = prompts.find(p => p.id === pid);
            if (prompt) {
                base.push({
                    id: `score_${pid}`,
                    label: prompt.name,
                    type: 'score',
                    promptId: pid,
                    className: 'rm-col-score',
                    hasAvgCheckbox: true,
                });
            }
        }

        // Average column (only if there are score columns)
        if (this._selectedPrompts.length > 0) {
            base.push({
                id: 'average',
                label: 'Avg',
                type: 'average',
                className: 'rm-col-score',
            });
        }

        base.push(
            { id: 'disposition', label: 'Disposition', type: 'disposition', className: 'rm-col-disposition' },
            { id: 'responseStatus', label: 'Response', type: 'responseStatus', className: 'rm-col-response' },
        );

        return base;
    }

    _defaultVisibility() {
        const vis = {};
        const allCols = ['expand', 'name', 'status', 'disposition', 'responseStatus'];
        for (const c of allCols) vis[c] = true;
        return vis;
    }

    _renderResponseStatus(candidate) {
        const status = candidate.responseStatus || 'no_response';
        const info = RESPONSE_STATUSES[status] || RESPONSE_STATUSES.no_response;
        let tooltip = info.label;
        if (status === 'wants_other_time' && candidate.proposedTimes?.length > 0) {
            tooltip += ': ' + candidate.proposedTimes.join(', ');
        }
        return `<span class="rm-response-badge" style="background:${info.color};color:#fff;padding:2px 8px;border-radius:10px;font-size:11px;white-space:nowrap;" title="${this._esc(tooltip)}">${info.label}</span>`;
    }

    _renderScore(scoreData) {
        if (!scoreData) return '<span class="rm-score rm-score-pending">\u2014</span>';
        if (scoreData.status === 'processing') return '<span class="rm-score rm-score-pending">...</span>';
        if (scoreData.status === 'error') return `<span class="rm-score rm-score-error" title="${this._esc(scoreData.error || '')}">err</span>`;
        const score = scoreData.score;
        if (score == null) return '<span class="rm-score rm-score-pending">\u2014</span>';
        const cls = score >= 7 ? 'high' : score >= 4 ? 'mid' : 'low';
        return `<span class="rm-score rm-score-${cls}" title="${this._esc(scoreData.reasoning || '')}">${score}</span>`;
    }

    _toLocalDatetime(date) {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        const h = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        return `${y}-${m}-${d}T${h}:${min}`;
    }

    _calcAverage(candidate) {
        if (this._avgPromptIds.size === 0) return -1;
        let sum = 0, count = 0;
        for (const pid of this._avgPromptIds) {
            const s = candidate.scores?.[pid];
            if (s?.status === 'complete' && s.score != null) { sum += s.score; count++; }
        }
        return count > 0 ? sum / count : -1;
    }

    _renderAverage(candidate) {
        if (this._avgPromptIds.size === 0) {
            return '<span class="rm-score rm-score-pending" title="Check score columns to include in average">\u2014</span>';
        }
        let sum = 0;
        let count = 0;
        for (const pid of this._avgPromptIds) {
            const s = candidate.scores?.[pid];
            if (s?.status === 'complete' && s.score != null) {
                sum += s.score;
                count++;
            }
        }
        if (count === 0) return '<span class="rm-score rm-score-pending">\u2014</span>';
        const avg = (sum / count).toFixed(1);
        const cls = avg >= 7 ? 'high' : avg >= 4 ? 'mid' : 'low';
        return `<span class="rm-score rm-score-${cls}" title="Average of ${count} criteria">${avg}</span>`;
    }

    _buildDispositionSelect(candidate) {
        const sel = document.createElement('select');
        sel.className = 'rm-disposition-select';
        const options = [
            ['', '\u2014'],
            ['contact-1st', 'Contact for 1st Interview'],
            ['waiting-1st', 'Waiting for 1st Interview'],
            ['downselect-1st', '1st Interview Down-select'],
            ['contact-2nd', 'Contact for 2nd Interview'],
            ['waiting-2nd', 'Waiting for 2nd Interview'],
            ['downselect-2nd', '2nd Interview Down-select'],
            ['invite-tour', 'Invite for Tour'],
            ['waiting-tour', 'Waiting for Tour'],
            ['waiting-no', 'Waiting to Send No'],
            ['downselect-hire', 'Down-select for Hire'],
            ['hold', 'Hold'],
            ['no', 'No'],
        ];
        for (const [val, label] of options) {
            const opt = document.createElement('option');
            opt.value = val;
            opt.textContent = label;
            if (candidate.disposition === val) opt.selected = true;
            sel.appendChild(opt);
        }
        sel.addEventListener('change', () => {
            this._onDispositionChange(candidate.id, sel.value);
        });
        return sel;
    }

    _getFilteredCandidates() {
        let list = [...this._candidates];

        // Hide rejected
        if (this._hideRejected) {
            list = list.filter(c => !c.rejected);
        }

        // Search filter
        if (this._searchQuery) {
            list = list.filter(c => {
                const searchable = [c.name, c.email, c.phone, c.education, c.careerSummary, c.skillSummary, c.fileName]
                    .filter(Boolean).join(' ').toLowerCase();
                return searchable.includes(this._searchQuery);
            });
        }

        // Sort
        list.sort((a, b) => {
            let va, vb;
            if (this._sortField === 'average') {
                va = this._calcAverage(a);
                vb = this._calcAverage(b);
            } else if (this._sortField.startsWith('score_')) {
                const pid = this._sortField.replace('score_', '');
                va = a.scores?.[pid]?.score ?? -1;
                vb = b.scores?.[pid]?.score ?? -1;
            } else if (this._sortField === 'interviewDate') {
                va = a.interviewDate || '';
                vb = b.interviewDate || '';
            } else if (['followUp', 'interview', 'rejected'].includes(this._sortField)) {
                va = a[this._sortField] ? 1 : 0;
                vb = b[this._sortField] ? 1 : 0;
            } else {
                va = (a[this._sortField] || '').toLowerCase();
                vb = (b[this._sortField] || '').toLowerCase();
            }
            const cmp = va < vb ? -1 : va > vb ? 1 : 0;
            return this._sortDir === 'asc' ? cmp : -cmp;
        });

        return list;
    }

    _toggleSort(field) {
        if (this._sortField === field) {
            this._sortDir = this._sortDir === 'asc' ? 'desc' : 'asc';
        } else {
            this._sortField = field;
            this._sortDir = 'asc';
        }
        this.render();
    }

    /** Build column visibility menu for toolbar */
    buildColumnMenu() {
        const cols = this._getColumns();
        const menu = document.createElement('div');
        menu.className = 'rm-column-menu';
        for (const col of cols) {
            if (!col.label) continue; // skip expand column
            const item = document.createElement('div');
            item.className = 'rm-column-menu-item';
            const cb = document.createElement('input');
            cb.type = 'checkbox';
            cb.checked = this._columnVisibility[col.id] !== false;
            cb.addEventListener('change', () => {
                this._columnVisibility[col.id] = cb.checked;
                this.render();
            });
            const label = document.createElement('span');
            label.textContent = col.label;
            item.appendChild(cb);
            item.appendChild(label);
            menu.appendChild(item);
        }
        return menu;
    }

    _startResize(e, th, colId) {
        const startX = e.pageX;
        const startWidth = th.offsetWidth;
        const table = th.closest('table');
        table.style.tableLayout = 'fixed';

        const onMove = (ev) => {
            const newWidth = Math.max(40, startWidth + ev.pageX - startX);
            th.style.width = newWidth + 'px';
            this._columnWidths[colId] = newWidth;
        };
        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }

    _openMarkdown(candidate) {
        if (!candidate.resumeMarkdown) return;
        const windowName = `resume_md_${candidate.id}`;
        const existing = this._resumeWindows.get(windowName);
        if (existing && !existing.closed) { existing.focus(); return; }

        const win = window.open('', windowName);
        if (!win) return;
        this._resumeWindows.set(windowName, win);

        const name = candidate.name || candidate.fileName;
        win.document.write(`<!DOCTYPE html><html><head>
            <meta charset="UTF-8">
            <title>${name} — Resume (Markdown)</title>
            <style>
                body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                       max-width: 800px; margin: 40px auto; padding: 0 20px; line-height: 1.6;
                       color: #1a1a2e; background: #fff; }
                h1 { font-size: 22px; border-bottom: 2px solid #e5e7eb; padding-bottom: 8px; }
                h2 { font-size: 18px; color: #374151; margin-top: 24px; }
                h3 { font-size: 15px; color: #6b7280; }
                ul { padding-left: 20px; }
                li { margin-bottom: 4px; }
                strong { color: #111827; }
                pre { background: #f3f4f6; padding: 12px; border-radius: 6px; overflow-x: auto; }
                code { background: #f3f4f6; padding: 2px 4px; border-radius: 3px; font-size: 13px; }
            </style>
        </head><body></body></html>`);
        win.document.close();

        const html = candidate.resumeMarkdown
            .replace(/^### (.+)$/gm, '<h3>$1</h3>')
            .replace(/^## (.+)$/gm, '<h2>$1</h2>')
            .replace(/^# (.+)$/gm, '<h1>$1</h1>')
            .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.+?)\*/g, '<em>$1</em>')
            .replace(/^- (.+)$/gm, '<li>$1</li>')
            .replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')
            .replace(/\n\n/g, '<br><br>')
            .replace(/\n/g, '\n');
        win.document.body.innerHTML = html;
    }

    async _openResume(candidate) {
        if (!candidate.storagePath) return;

        const existing = this._resumeWindows.get(candidate.id);
        if (existing && !existing.closed) {
            existing.focus();
            return;
        }

        try {
            // Get current user's ID token for authenticated access
            const user = firebase.auth().currentUser;
            if (!user) return;
            const idToken = await user.getIdToken();

            // Use the serveResume cloud function instead of public download URL
            const functionUrl = `https://us-central1-ravenresume-app.cloudfunctions.net/serveResume`;
            const url = `${functionUrl}?path=${encodeURIComponent(candidate.storagePath)}`;

            // Open in new window with auth header via fetch + blob URL
            const response = await fetch(url, {
                headers: { 'Authorization': `Bearer ${idToken}` },
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const win = window.open(blobUrl, `resume_${candidate.id}`);
            if (win) {
                this._resumeWindows.set(candidate.id, win);
                // Clean up blob URL when window closes
                const check = setInterval(() => {
                    if (win.closed) {
                        URL.revokeObjectURL(blobUrl);
                        clearInterval(check);
                    }
                }, 5000);
            }
        } catch (err) {
            console.error('Failed to open resume:', err);
        }
    }

    _esc(str) {
        const d = document.createElement('div');
        d.textContent = str || '';
        return d.innerHTML;
    }
}
